<template>
    <Head title="Terms & Consent" />
    <div class="flex flex-col min-h-screen overflow-y-auto h-screen bg-valence-neutral-light text-valence-grey-800">
        <div class="fade-in grow justify-center gap-5 md:gap-8 flex flex-col items-center py-4 md:py-10">
            <div class="flex flex-col gap-4 justify-around text-center max-w-sm mx-auto px-4">
                <div class="flex flex-col pb-5 justify-center">
                    <img :src="Logo" alt="Valence logo" class="mx-auto h-16 w-auto" />
                </div>
            </div>
            <template v-if="!termsConsented">
                <div class="flex flex-col gap-4 items-center max-w-md">
                    <div class="text-center mb-4">
                        <h2 class="title-body mb-2">{{ copy.heading }}</h2>
                        <p class="body-small text-gray-600">{{ copy.body }}</p>
                    </div>

                    <div class="max-w-md mx-auto w-full">
                        <TeamsTerms v-model:terms="terms" :terms-content="org_terms_content" :modal="modal_terms" @accept="acceptTerms" />
                    </div>

                    <LoadingSpinner v-if="loading" class="w-10 h-10" />
                    <button
                        v-else-if="!modal_terms"
                        :disabled="!terms"
                        class="w-full py-3 px-4 bg-valence-pink text-white rounded-lg hover:opacity-90 transition-all duration-150 font-medium disabled:opacity-50 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        @click="acceptTerms">
                        Continue
                    </button>
                </div>
            </template>
            <template v-else>
                <LoadingSpinner v-if="loading" class="w-10 h-10" />
                <div v-else class="rounded-lg max-w-md py-4 text-center">
                    <p>You're logged in.</p>
                    <p>You may close this page and go back to the app.</p>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { Head } from "@inertiajs/vue3";
import Logo from "~img/color-logo.svg";
import TeamsTerms from "~vue/components/TermsConsent.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { submitFormNavigation } from "~vue/utils/formPost";
import { logError } from "~vue/utils/logUtils";
import { computed, ref } from "vue";

const { token, terms_document_id, reconsent_reason } = defineProps({
    token: { type: String, default: null },
    org_terms_content: { type: String, default: null },
    terms_document_id: { type: String, required: true },
    modal_terms: { type: Boolean, default: false },
    reconsent_reason: { type: String, default: "first_time" },
});

// The heading carries the re-consent framing here, so the shared TermsConsent component
// is deliberately not passed the reason (its inline notice would double the message).
const RECONSENT_COPY = {
    first_time: { heading: "Terms of Consent Required", body: "Please accept the terms to complete your login." },
    updated: {
        heading: "Our Terms Have Been Updated",
        body: "The terms have been updated since you last accepted them. Please review and accept them to complete your login.",
    },
    grandfathered: {
        heading: "Please Re-accept the Terms",
        body: "We've improved our records around terms acceptance. Please re-accept the terms to complete your login.",
    },
};

const copy = computed(() => RECONSENT_COPY[reconsent_reason] ?? RECONSENT_COPY.first_time);

const loading = ref(false);
const terms = ref(false);
const termsConsented = ref(false);

async function acceptTerms() {
    if (!terms.value) {
        return;
    }

    loading.value = true;

    try {
        const response = await fetch("/microsoft-teams/terms-of-consent/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
                consent: true,
                terms_document_id,
                oauth_redirect: true,
                token,
            }),
        });

        if (response.ok) {
            const { oauth_form_post, redirect } = await response.json();
            // Re-entering the OAuth flow POSTs the token so a ~2.2 KB Entra JWT can't be
            // truncated by a URL-length cap (BRA-895). `redirect` still carries the
            // cross-server hand-off, which is short and stays a plain navigation.
            if (oauth_form_post) {
                submitFormNavigation(oauth_form_post.action, oauth_form_post.fields);
                return;
            }
            if (redirect) {
                globalThis.location.href = redirect;
            }
        } else {
            logError("Failed to submit terms consent", { sendToSlack: true });
        }
    } catch (error) {
        logError(error, { sendToSlack: true });
    } finally {
        loading.value = false;
    }
}
</script>
